import { useEffect, useState } from "react";
import { Avatar, Box, Collapse, IconButton, Link, Menu, Tooltip, Typography, useMediaQuery } from "@mui/material";
import type { Theme } from "@mui/material/styles";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Link as RouterLink } from "react-router-dom";
import { Tab, useRecordContext } from "react-admin";
import { grants } from "../backoffice.access_control";
import { OpenInFirestoreMenuItem } from "../menu-items/OpenInFirestoreMenuItem";
import { OpenInHubspotMenuItem } from "../menu-items/OpenInHubspotMenuItem";
import { ActiveStudentsField } from "../fields/ActiveStudentsField";
import { InstructorCalendar } from "../misc/calendar/InstructorCalendar";
import { Instructor } from "../providers/instructorsProvider";
import { overviewCardStyle } from "../misc/backofficeTheme";
import { ShowBase } from "../misc/ShowBase";
import { InstructorAdminForm } from "./instructor/InstructorAdminForm";
import { Tabs } from "../misc/Tabs";
import { Row } from "../misc/Row";
import { LoadingIndicator } from "../misc/LoadingIndicator";
import { useCurrentDrivingSchool } from "../hooks/useCurrentDrivingSchool";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { NameHeader } from "../misc/NameHeader";
import { HeaderField } from "../misc/HeaderField";
import { InstructorMap } from "../misc/maps";
import { InstructorStudentsList } from "./instructor/InstructorStudentsList";
import { Tab2 } from "../misc/Tab2";
import { InstructorAppointmentsList } from "./instructor/InstructorAppointmentsList";
import { useMenu } from "../hooks/useMenu";
import { useRecordAndSwitchDrivingSchoolIfNecessary } from "../hooks/useRecordAndSwitchDrivingSchoolIfNecessary";
import { InstructorVehiclesList } from "./instructor/InstructorVehiclesList";
import { CopyIdIntoClipboardMenuItem } from "../menu-items/CopyIdIntoClipboardMenuItem";
import { CopyVogelIdIntoClipboardMenuItem } from "../menu-items/CopyVogelIdIntoClipboardMenuItem";
import { PhoneNumberField } from "../fields/PhoneNumberField";
import { Column } from "../misc/Column";
import { BackButton } from "../buttons/BackButton";
import { InstructorAvailabilitySettingsTab } from "./instructor/InstructorAvailabilitySettingsTab";
import { useDialog } from "../hooks/useDialog";
import { ViewInAppNotificationsMenuItem } from "../menu-items/ViewInAppNotificationsMenuItem";
import { InAppNotificationsDialog } from "../dialogs/InAppNotificationsDialog";
import { isDefined } from "../utils/filters";

export function InstructorDetails() {
  useEffect(() => {
    // For backwards compatibility ...
    if (location.pathname.endsWith("/calendar")) {
      const url = new URL(location.href);
      url.pathname = url.pathname.substring(0, url.pathname.length - "/calendar".length);
      location.replace(url.toString());
    }
  }, []);
  if (location.pathname.endsWith("/calendar")) {
    return null;
  }
  return (
    <ShowBase>
      <InstructorDetailsTabs />
    </ShowBase>
  );
}

function InstructorButtons() {
  return (
    <Row sx={{ display: "block" }}>
      <Box sx={{ display: "flex" }}>{grants.has("admin") && <MoreMenu />}</Box>
    </Row>
  );
}

function MoreMenu() {
  const { anchorEl, open, openMenu, closeMenu } = useMenu();
  const { dialogProps, openDialog } = useDialog("InAppNotificationsDialog");
  return (
    <>
      <IconButton aria-label="Weitere Aktionen" onClick={openMenu}>
        <MoreVertIcon />
      </IconButton>
      <Menu anchorEl={anchorEl} open={open} onClose={closeMenu}>
        {grants.has("viewInAppNotifications") && (
          <ViewInAppNotificationsMenuItem closeMenu={closeMenu} openDialog={openDialog} />
        )}
        {grants.has("viewHubspot") && <OpenInHubspotMenuItem closeMenu={closeMenu} />}
        {grants.has("developer") && <OpenInFirestoreMenuItem closeMenu={closeMenu} />}
        {grants.has("developer") && <CopyIdIntoClipboardMenuItem closeMenu={closeMenu} />}
        {grants.has("developer") && <CopyVogelIdIntoClipboardMenuItem closeMenu={closeMenu} />}
      </Menu>
      <InAppNotificationsDialog {...dialogProps} />
    </>
  );
}

function InstructorEmailField() {
  const instructor = useRecordContext<Instructor>();
  const href =
    grants.has("viewHubspot") && instructor.hubspotContactId
      ? `https://app.hubspot.com/contacts/19951845/record/0-1/${instructor.hubspotContactId}/?interaction=email`
      : `mailto:${instructor.email}`;

  return (
    <Typography variant="body2">
      <Link href={href} target="_blank" rel="noopener noreferrer">
        {instructor.email}
      </Link>
    </Typography>
  );
}

function InstructorDetailsHeader({ instructor }: { instructor: Instructor }) {
  const isDesktop = useMediaQuery((theme: Theme) => theme.breakpoints.up("md"));
  const [showDetails, setShowDetails] = useState(false);
  const detailsExpanded = isDesktop || showDetails;
  const initials = instructor.name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((namePart) => namePart[0])
    .join("")
    .toUpperCase();

  return (
    <Column>
      <Row sx={{ alignItems: "center", justifyContent: "space-between", my: { xs: 1.5, sm: "15px" } }}>
        <BackButton
          url={`/drivingSchools/${instructor.drivingSchoolId}/employees#${instructor.isActive ? "instructors" : "inactive-instructors"}`}
        >
          {instructor.isActive ? "Fahrlehrer" : "Fahrlehrer (inaktiv)"}
        </BackButton>
        <InstructorButtons />
      </Row>
      <Box
        sx={{
          alignItems: "flex-start",
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          gap: { xs: detailsExpanded ? 2.5 : 0, md: "50px" },
          mb: "20px",
        }}
      >
        <Row sx={{ alignItems: "center", gap: { xs: 1.5, sm: 2.5 }, minWidth: 0, width: { xs: "100%", md: "auto" } }}>
          <Avatar
            alt={instructor.name}
            src={instructor.avatarUrl}
            sx={{ flexShrink: 0, height: { xs: 48, sm: 60 }, width: { xs: 48, sm: 60 } }}
          >
            {initials}
          </Avatar>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              minWidth: 0,
              "& h2": {
                fontSize: { xs: "22px", sm: "24px" },
                lineHeight: 1.15,
                overflowWrap: "anywhere",
              },
            }}
          >
            <NameHeader>{instructor.name}</NameHeader>
            <Typography color="text.secondary" sx={{ fontSize: "14px", lineHeight: 1.43 }}>
              {instructor.isDrivingSchoolManager ? "Fahrschulleiter" : "Fahrlehrer"}
            </Typography>
          </Box>
          <IconButton
            aria-expanded={detailsExpanded}
            aria-label={detailsExpanded ? "Profildetails ausblenden" : "Profildetails anzeigen"}
            onClick={() => setShowDetails((visible) => !visible)}
            sx={{ display: { xs: "inline-flex", md: "none" }, ml: "auto" }}
          >
            <ExpandMoreIcon
              sx={{ transform: detailsExpanded ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 200ms" }}
            />
          </IconButton>
        </Row>
        <Collapse
          in={detailsExpanded}
          sx={{ flex: { md: "1 1 auto" }, minWidth: 0, width: { xs: "100%", md: "auto" } }}
        >
          <Box
            data-testid="instructor-details-fields"
            sx={{
              columnGap: { xs: 2, md: "50px" },
              display: "grid",
              gridTemplateColumns: { xs: "repeat(2, minmax(0, 1fr))", md: "repeat(4, minmax(0, max-content))" },
              minWidth: 0,
              pt: { xs: 2, md: 0 },
              rowGap: 1,
              width: "100%",
            }}
          >
            <Box sx={{ minWidth: 0 }}>
              <HeaderField label="aktive Fahrschüler">
                <ActiveStudentsField />
              </HeaderField>
            </Box>
            {instructor.phoneNumber && (
              <Box sx={{ minWidth: 0 }}>
                <HeaderField label="Telefonnummer">
                  <PhoneNumberField source="phoneNumber" />
                </HeaderField>
              </Box>
            )}
            {instructor.email && (
              <Box
                sx={{
                  minWidth: 0,
                  "& a": { overflowWrap: "anywhere" },
                }}
              >
                <HeaderField label="E-Mail">
                  <InstructorEmailField />
                </HeaderField>
              </Box>
            )}
            <Box sx={{ minWidth: 0 }}>
              <InstructorBranchesField />
            </Box>
          </Box>
        </Collapse>
      </Box>
    </Column>
  );
}

function InstructorBranchesField() {
  const instructor = useRecordContext<Instructor>();
  const { branches } = useCurrentDrivingSchool();
  const { assignedBranches: assignedBranchIds } = instructor;

  const assignedBranches = assignedBranchIds.map((id) => branches.find((branch) => branch.id === id)).filter(isDefined);
  const [firstBranch, ...additionalBranches] = assignedBranches;

  if (branches.length <= 1) {
    return null;
  }

  return (
    <HeaderField label="Filiale">
      {branches.length === 0 || assignedBranches.length === 0 ? (
        <Typography sx={{ fontSize: "14px" }} color="text.secondary">
          Keine Filiale zugewiesen
        </Typography>
      ) : (
        <Tooltip
          title={
            <Column>
              {assignedBranches.map((branch) => (
                <Typography key={branch.id} sx={{ fontSize: "12px", color: "inherit" }}>
                  {branch.name}
                </Typography>
              ))}
            </Column>
          }
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: "4px" }}>
            <Link
              component={RouterLink}
              to={`/branches/${firstBranch.id}`}
              sx={{
                fontSize: "14px",
                textAlign: "left",
              }}
            >
              <span>{firstBranch.name}</span>
            </Link>
            {additionalBranches.length > 0 && (
              <Typography color="text.secondary" sx={{ fontSize: "14px" }}>
                + {additionalBranches.length}
              </Typography>
            )}
          </Box>
        </Tooltip>
      )}
    </HeaderField>
  );
}

function InstructorDetailsTabs() {
  const instructor = useRecordAndSwitchDrivingSchoolIfNecessary<Instructor>((it) => it.drivingSchoolId);

  if (!instructor) {
    return <LoadingIndicator />;
  }

  return (
    <Tabs
      sx={{ ...overviewCardStyle, padding: { xs: "5px 16px 16px", sm: "5px 30px 10px" } }}
      header={<InstructorDetailsHeader instructor={instructor} />}
      mobileSelectAriaLabel="Profilbereich"
      separateCards
      className="InstructorDetailsTabs"
    >
      <Tab label="Kalender" /* no path attribute here, so the tab is shown by default */>
        <InstructorCalendar />
      </Tab>
      <Tab label="Terminliste" path="appointments">
        <InstructorAppointmentsList />
      </Tab>
      <Tab label="Fahrschüler" path="students">
        <InstructorStudentsList />
      </Tab>
      <Tab2 label="Karte" path="map">
        <Box
          data-testid="instructor-map-container"
          sx={{ ...overviewCardStyle, height: "100%", minWidth: 0, padding: { xs: 2, sm: 3 } }}
        >
          <InstructorMap instructor={instructor} />
        </Box>
      </Tab2>
      <Tab label="Fahrzeuge" path="vehicles">
        <InstructorVehiclesList />
      </Tab>
      {grants.has("Instructor:editAvailabilities") && (
        <Tab label="Verfügbarkeiten" path="availabilities">
          <InstructorAvailabilitySettingsTab />
        </Tab>
      )}
      {(grants.has("manageUsers") || grants.has("Instructor:assignBranches")) && (
        <Tab label="Einstellungen" path="admin">
          <InstructorAdminForm />
        </Tab>
      )}
    </Tabs>
  );
}
